import * as pdf from 'react-pdf';
import React, { useRef, useState, useEffect } from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Card, IconButton, Typography, CardContent } from '@mui/material';

import useLocales from '../../../hooks/useLocales';
import encryptIDWithRsaPublicKey from '../../../utils/documents/encryptIDWithRsaPublicKey';

const { Page, Document } = pdf;

// --------------------------------------------------------

type PDFViewerDocumentProps = {
  accessId: string;
};

const PDFViewerDocument: React.FC<PDFViewerDocumentProps> = ({ accessId }) => {
  const { t } = useLocales();
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [layerWidth, setLayerWidth] = useState<number>(100);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  // Encrypt accessId to get the actual URL
  useEffect(() => {
    if (accessId) {
      const url = encryptIDWithRsaPublicKey(accessId);
      if (url) {
        setFileUrl(url);
      }
    }
  }, [accessId]);

  // Handle container resize to calculate PDF width
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setLayerWidth(containerRef.current.offsetWidth);
      }
    };

    // Only setup observer when fileUrl is ready (component is rendered)
    if (!fileUrl) return;

    // Initial size calculation
    handleResize();

    // Use ResizeObserver to watch for container size changes
    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(containerRef.current);
    } else {
      // Fallback to window resize event
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [fileUrl]);

  const handlePdfNavButtonClick = (i: number) => {
    setPageNumber(pageNumber + i);
  };

  if (!fileUrl) {
    return null;
  }

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mb: 1 }}>
        <IconButton
          aria-label="arrow-back"
          size="large"
          disabled={pageNumber === 1}
          onClick={() => handlePdfNavButtonClick(-1)}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography>
          {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
          {numPages}
        </Typography>

        <IconButton
          aria-label="arrow-forward"
          size="large"
          disabled={pageNumber === numPages}
          onClick={() => handlePdfNavButtonClick(1)}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
      <CardContent sx={{ maxHeight: '100%', p: 0 }}>
        {/* PDF DOCUMENT */}
        <Box ref={containerRef} sx={{ width: '100%' }}>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            externalLinkTarget="_blank"
          >
            <Page pageNumber={pageNumber} width={layerWidth} renderTextLayer={false} />
          </Document>
        </Box>
      </CardContent>

      {/* PDF NAVIGATION */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mt: 1 }}>
        <IconButton
          aria-label="arrow-back"
          size="large"
          disabled={pageNumber === 1}
          onClick={() => handlePdfNavButtonClick(-1)}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography>
          {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
          {numPages}
        </Typography>

        <IconButton
          aria-label="arrow-forward"
          size="large"
          disabled={pageNumber === numPages}
          onClick={() => handlePdfNavButtonClick(1)}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </Card>
  );
};

export default PDFViewerDocument;
